import { Slide } from '../components/SlideComponents/slide.types';
import { AiAssessedScore } from '../frontendServices/gameAPI';

type ScoreLike = Pick<AiAssessedScore, 'responseId' | 'aiScore'>;

/**
 * Scores that feed the scoreboard bar chart. One entry per assessed response
 * whose response is still visible — i.e. present in `visibleResponseIds`.
 * Dismissed responses are filtered out of the response list upstream, so their
 * ids are absent from the set and their scores drop out here. Pass `undefined`
 * to skip filtering entirely (e.g. while the response list is still loading, so
 * scores aren't briefly hidden). A missing / non-finite aiScore counts as 0 —
 * the response was scored, just not numerically resolvable.
 */
export function computeScoreboardScores(
  aiAssessedScores: ScoreLike[] | undefined,
  visibleResponseIds?: ReadonlySet<string>,
): number[] {
  if (!aiAssessedScores) return [];
  const visible = visibleResponseIds
    ? aiAssessedScores.filter(a => visibleResponseIds.has(a.responseId))
    : aiAssessedScores;
  return visible.map(a =>
    typeof a.aiScore === 'number' && Number.isFinite(a.aiScore) ? a.aiScore : 0,
  );
}

export const MAX_SCORE_NONE_VALUE = 'none';
export const MAX_SCORE_UNLIMITED = -1;

export type MaxScoreSelectValue = typeof MAX_SCORE_NONE_VALUE | number;
export type MaxScoreOption = { value: MaxScoreSelectValue; displayName: string };

export const MAX_SCORE_OPTIONS: MaxScoreOption[] = [
  { value: MAX_SCORE_NONE_VALUE, displayName: 'None' },
  { value: MAX_SCORE_UNLIMITED, displayName: 'Unlimited' },
  // Start at 1: a `maxScore` of 0 produces a degenerate single-bucket chart
  // (every response clamped to score=0) and still activates the scoreboard
  // flow, which is confusing. Teachers who want to disable scoring should
  // pick "None".
  ...Array.from({ length: 25 }, (_, i) => ({
    value: i + 1,
    displayName: String(i + 1),
  })),
];

export function maxScoreToSelectValue(
  maxScore: number | null | undefined,
): MaxScoreSelectValue {
  return maxScore == null ? MAX_SCORE_NONE_VALUE : maxScore;
}

export function selectValueToMaxScore(
  value: MaxScoreSelectValue | string | number | boolean,
): number | null {
  return value === MAX_SCORE_NONE_VALUE ? null : (value as number);
}

export function hasScorePromptOutput(slide: Slide | undefined | null): boolean {
  if (!slide?.prompts) return false;
  return slide.prompts.some(prompt => {
    const properties = prompt.promptOutput?.properties;
    if (!properties) return false;
    return Object.values(properties).some(property => {
      if (!property) return false;
      if (property.id === 'score') return true;
      const description = property.description?.trim().toLowerCase();
      return description === 'score';
    });
  });
}
